"use client";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth, type StaffRole } from '@/contexts/AuthContext';
import { memberService, visitorService, type Member, type Visitor } from '@/lib/supabase-service';
import { createClient } from '@/lib/supabase/client';

// ── Types ──────────────────────────────────────────────────────────────────
type ReportType = 'weekly_attendance' | 'visitor_followup' | 'pipeline_status' | 'department_roster' | 'monthly_summary';
type ActiveTab = 'members' | 'visitors' | 'staff' | 'email_reports';

interface StaffMember {
  id: string;
  email: string;
  fullName: string;
  role: StaffRole;
  createdAt: string;
}

interface ScheduleConfig {
  weeklyEnabled: boolean;
  weeklyDay: number;
  monthlyEnabled: boolean;
  monthlyDay: number;
  pastorEmail: string;
  pastorName: string;
  businessEmail: string;
  businessName: string;
}

interface ReportConfig {
  type: ReportType;
  label: string;
  description: string;
  icon: React.ReactNode;
}

// ── Constants ──────────────────────────────────────────────────────────────
const SCHEDULE_STORAGE_KEY = 'compassion_report_schedule';
const LAST_WEEKLY_SENT_KEY = 'compassion_last_weekly_sent';
const LAST_MONTHLY_SENT_KEY = 'compassion_last_monthly_sent';
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DEFAULT_SCHEDULE: ScheduleConfig = {
  weeklyEnabled: false,
  weeklyDay: 0,
  monthlyEnabled: false,
  monthlyDay: 1,
  pastorEmail: 'pastor@church.org',
  pastorName: 'Pastor',
  businessEmail: 'business@church.org',
  businessName: 'Business Office',
};

const PIPELINE_LABELS: Record<Member['pipelineStage'], string> = {
  visitor: 'First Visit',
  membership_class: 'Membership Class',
  full_member: 'Full Member',
};

const ROLE_LABELS: Record<StaffRole, string> = {
  admin: 'Admin',
  church_staff: 'Church Staff',
  greeter: 'Greeter',
  usher: 'Usher',
  pastor: 'Pastor',
  business_office: 'Business Office',
};

const ROLE_COLORS: Record<StaffRole, string> = {
  admin: 'text-red-400 border-red-400/40 bg-red-400/10',
  pastor: 'text-purple-400 border-purple-400/40 bg-purple-400/10',
  church_staff: 'text-blue-400 border-blue-400/40 bg-blue-400/10',
  greeter: 'text-gold border-gold/40 bg-gold/10',
  usher: 'text-green-400 border-green-400/40 bg-green-400/10',
  business_office: 'text-orange-400 border-orange-400/40 bg-orange-400/10',
};

// ── CSV Export Utility ─────────────────────────────────────────────────────
function exportCSV(filename: string, headers: string[], rows: (string | number | boolean)[][][]) {
  const escape = (v: string | number | boolean) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(escape).join(','), ...rows.map(r => r.flat().map(escape).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Email HTML Builders ────────────────────────────────────────────────────
function buildWeeklyAttendanceHtml(members: Member[]): string {
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);
  const weekAgoStr = weekAgo.toISOString().split('T')[0];
  const attended = members.filter(m => m.lastVisit >= weekAgoStr);
  const rows = attended.map(m => `<tr><td style="padding:8px 12px;border-bottom:1px solid #2a3a5c;">${m.name}</td><td style="padding:8px 12px;border-bottom:1px solid #2a3a5c;">${m.lastVisit}</td><td style="padding:8px 12px;border-bottom:1px solid #2a3a5c;">${m.department || '—'}</td></tr>`).join('');
  return `<div style="font-family:monospace;background:#0A1628;color:#C8D6E5;padding:32px;max-width:600px;">
    <h1 style="color:#D4A843;font-size:20px;margin-bottom:4px;">COMPASSION CHECK-IN CHURCH MANAGEMENT</h1>
    <h2 style="color:#C8D6E5;font-size:14px;margin-bottom:24px;opacity:0.7;">Weekly Attendance Summary — ${today.toDateString()}</h2>
    <div style="background:#0d1f3c;padding:16px;margin-bottom:24px;">
      <span style="color:#D4A843;font-size:28px;font-weight:bold;">${attended.length}</span>
      <span style="color:#7a8fa6;font-size:12px;margin-left:8px;">members attended this week</span>
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead><tr style="background:#0d1f3c;"><th style="padding:8px 12px;text-align:left;color:#D4A843;">Name</th><th style="padding:8px 12px;text-align:left;color:#D4A843;">Last Visit</th><th style="padding:8px 12px;text-align:left;color:#D4A843;">Department</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="3" style="padding:12px;color:#7a8fa6;">No attendance recorded this week.</td></tr>'}</tbody>
    </table>
    <p style="color:#7a8fa6;font-size:11px;margin-top:24px;">Total active members: ${members.filter(m => m.status === 'Active').length} | Generated by Compassion Check-in</p>
  </div>`;
}

function buildVisitorFollowupHtml(visitors: Visitor[]): string {
  const pending = visitors.filter(v => !v.followedUp);
  const rows = pending.map(v => `<tr><td style="padding:8px 12px;border-bottom:1px solid #2a3a5c;">${v.name}</td><td style="padding:8px 12px;border-bottom:1px solid #2a3a5c;">${v.date}</td><td style="padding:8px 12px;border-bottom:1px solid #2a3a5c;">${v.phone}</td><td style="padding:8px 12px;border-bottom:1px solid #2a3a5c;">${v.interestedIn.join(', ') || '—'}</td></tr>`).join('');
  return `<div style="font-family:monospace;background:#0A1628;color:#C8D6E5;padding:32px;max-width:600px;">
    <h1 style="color:#D4A843;font-size:20px;margin-bottom:4px;">COMPASSION CHECK-IN CHURCH MANAGEMENT</h1>
    <h2 style="color:#C8D6E5;font-size:14px;margin-bottom:24px;opacity:0.7;">Visitor Follow-Up List — ${new Date().toDateString()}</h2>
    <div style="background:#0d1f3c;padding:16px;margin-bottom:24px;">
      <span style="color:#D4A843;font-size:28px;font-weight:bold;">${pending.length}</span>
      <span style="color:#7a8fa6;font-size:12px;margin-left:8px;">visitors awaiting follow-up</span>
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead><tr style="background:#0d1f3c;"><th style="padding:8px 12px;text-align:left;color:#D4A843;">Name</th><th style="padding:8px 12px;text-align:left;color:#D4A843;">Visit Date</th><th style="padding:8px 12px;text-align:left;color:#D4A843;">Phone</th><th style="padding:8px 12px;text-align:left;color:#D4A843;">Interests</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="4" style="padding:12px;color:#7a8fa6;">All visitors have been followed up!</td></tr>'}</tbody>
    </table>
    <p style="color:#7a8fa6;font-size:11px;margin-top:24px;">Total visitors: ${visitors.length} | Followed up: ${visitors.filter(v => v.followedUp).length} | Generated by Compassion Check-in</p>
  </div>`;
}

function buildPipelineStatusHtml(members: Member[]): string {
  const visitor = members.filter(m => m.pipelineStage === 'visitor');
  const classes = members.filter(m => m.pipelineStage === 'membership_class');
  const full = members.filter(m => m.pipelineStage === 'full_member');
  return `<div style="font-family:monospace;background:#0A1628;color:#C8D6E5;padding:32px;max-width:600px;">
    <h1 style="color:#D4A843;font-size:20px;margin-bottom:4px;">COMPASSION CHECK-IN CHURCH MANAGEMENT</h1>
    <h2 style="color:#C8D6E5;font-size:14px;margin-bottom:24px;opacity:0.7;">Intake Pipeline Status — ${new Date().toDateString()}</h2>
    ${[{label:'First Visit',count:visitor.length,members:visitor,color:'#D4A843'},{label:'Membership Class',count:classes.length,members:classes,color:'#60a5fa'},{label:'Full Member',count:full.length,members:full,color:'#4ade80'}].map(stage => `
    <div style="background:#0d1f3c;padding:16px;margin-bottom:16px;border-left:3px solid ${stage.color};">
      <div style="color:${stage.color};font-size:12px;text-transform:uppercase;letter-spacing:0.2em;margin-bottom:8px;">${stage.label} — ${stage.count}</div>
      ${stage.members.map(m => `<div style="color:#C8D6E5;font-size:12px;padding:2px 0;">${m.name}${m.department ? ` · ${m.department}` : ''}</div>`).join('') || '<div style="color:#7a8fa6;font-size:12px;">None</div>'}
    </div>`).join('')}
    <p style="color:#7a8fa6;font-size:11px;margin-top:24px;">Total in pipeline: ${members.length} | Generated by Compassion Check-in</p>
  </div>`;
}

function buildDepartmentRosterHtml(members: Member[]): string {
  const depts = [...new Set(members.map(m => m.department).filter(Boolean))];
  const deptSections = depts.map(dept => {
    const deptMembers = members.filter(m => m.department === dept);
    return `<div style="background:#0d1f3c;padding:16px;margin-bottom:16px;">
      <div style="color:#D4A843;font-size:12px;text-transform:uppercase;letter-spacing:0.2em;margin-bottom:4px;">${dept}</div>
      <div style="color:#7a8fa6;font-size:11px;margin-bottom:8px;">${deptMembers.length} members</div>
      ${deptMembers.map(m => `<div style="color:#C8D6E5;font-size:12px;padding:2px 0;">${m.name}</div>`).join('') || '<div style="color:#7a8fa6;font-size:12px;">No members assigned</div>'}
    </div>`;
  }).join('');
  return `<div style="font-family:monospace;background:#0A1628;color:#C8D6E5;padding:32px;max-width:600px;">
    <h1 style="color:#D4A843;font-size:20px;margin-bottom:4px;">COMPASSION CHECK-IN CHURCH MANAGEMENT</h1>
    <h2 style="color:#C8D6E5;font-size:14px;margin-bottom:24px;opacity:0.7;">Department Rosters — ${new Date().toDateString()}</h2>
    ${deptSections}
    <p style="color:#7a8fa6;font-size:11px;margin-top:24px;">Total departments: ${depts.length} | Generated by Compassion Check-in</p>
  </div>`;
}

function buildMonthlySummaryHtml(members: Member[], visitors: Visitor[]): string {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const newMembers = members.filter(m => m.joinDate >= monthStart);
  const monthVisitors = visitors.filter(v => v.date >= monthStart);
  return `<div style="font-family:monospace;background:#0A1628;color:#C8D6E5;padding:32px;max-width:600px;">
    <h1 style="color:#D4A843;font-size:20px;margin-bottom:4px;">COMPASSION CHECK-IN CHURCH MANAGEMENT</h1>
    <h2 style="color:#C8D6E5;font-size:14px;margin-bottom:24px;opacity:0.7;">Monthly Summary — ${now.toLocaleString('default', { month: 'long', year: 'numeric' })}</h2>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:24px;">
      ${[
        {label:'Total Members',value:members.length,color:'#D4A843'},
        {label:'Active Members',value:members.filter(m=>m.status==='Active').length,color:'#4ade80'},
        {label:'New This Month',value:newMembers.length,color:'#60a5fa'},
        {label:'Visitors This Month',value:monthVisitors.length,color:'#D4A843'},
        {label:'Awaiting Follow-Up',value:visitors.filter(v=>!v.followedUp).length,color:'#f87171'},
        {label:'In Pipeline',value:members.filter(m=>m.pipelineStage!=='full_member').length,color:'#a78bfa'},
      ].map(s=>`<div style="background:#0d1f3c;padding:16px;"><div style="color:${s.color};font-size:24px;font-weight:bold;">${s.value}</div><div style="color:#7a8fa6;font-size:11px;text-transform:uppercase;letter-spacing:0.1em;">${s.label}</div></div>`).join('')}
    </div>
    <p style="color:#7a8fa6;font-size:11px;">Generated by Compassion Check-in Church Management System</p>
  </div>`;
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function ReportsPage() {
  const router = useRouter();
  const { user, profile, loading: authLoading } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<ActiveTab>('members');

  // Data
  const [members, setMembers] = useState<Member[]>([]);
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Member filters
  const [memberSearch, setMemberSearch] = useState('');
  const [memberStatusFilter, setMemberStatusFilter] = useState<'All' | Member['status']>('All');
  const [memberPipelineFilter, setMemberPipelineFilter] = useState<'All' | Member['pipelineStage']>('All');

  // Visitor filters
  const [visitorSearch, setVisitorSearch] = useState('');
  const [visitorFollowFilter, setVisitorFollowFilter] = useState<'All' | 'Pending' | 'Followed Up'>('All');

  // Staff filters
  const [staffSearch, setStaffSearch] = useState('');
  const [staffRoleFilter, setStaffRoleFilter] = useState<'All' | StaffRole>('All');

  // Email reports
  const [sending, setSending] = useState<ReportType | null>(null);
  const [results, setResults] = useState<Record<string, { success: boolean; message: string }>>({});
  const [customEmail, setCustomEmail] = useState('');

  // Schedule
  const [schedule, setSchedule] = useState<ScheduleConfig>(DEFAULT_SCHEDULE);
  const [scheduleSaved, setScheduleSaved] = useState(false);
  const [scheduleStatus, setScheduleStatus] = useState<{ weekly: string; monthly: string }>({ weekly: '', monthly: '' });
  const [sendingScheduled, setSendingScheduled] = useState<'weekly' | 'monthly' | null>(null);

  const getScheduledRecipients = useCallback((cfg: ScheduleConfig) => {
    return [
      { email: cfg.pastorEmail, name: cfg.pastorName },
      { email: cfg.businessEmail, name: cfg.businessName },
    ].filter(r => r.email.trim() !== '');
  }, []);

  const sendScheduledReport = useCallback(async (
    type: 'weekly' | 'monthly',
    cfg: ScheduleConfig,
    currentMembers: Member[],
    currentVisitors: Visitor[],
  ) => {
    const recipients = getScheduledRecipients(cfg);
    if (recipients.length === 0) return;
    const now = new Date();
    let subject = '';
    let htmlContent = '';
    if (type === 'weekly') {
      subject = `Weekly Attendance Summary — ${now.toDateString()}`;
      htmlContent = buildWeeklyAttendanceHtml(currentMembers);
    } else {
      subject = `Monthly Summary — ${now.toLocaleString('default', { month: 'long', year: 'numeric' })}`;
      htmlContent = buildMonthlySummaryHtml(currentMembers, currentVisitors);
    }
    try {
      const res = await fetch('/api/scheduled-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportType: type === 'weekly' ? 'weekly_attendance' : 'monthly_summary', subject, htmlContent, recipients }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const sentKey = type === 'weekly' ? LAST_WEEKLY_SENT_KEY : LAST_MONTHLY_SENT_KEY;
        localStorage.setItem(sentKey, now.toISOString().split('T')[0]);
        return { success: true, message: `Sent to ${recipients.length} recipient(s). ID: ${data.messageId || 'OK'}` };
      } else {
        return { success: false, message: data.error || 'Failed to send.' };
      }
    } catch (err: unknown) {
      return { success: false, message: err instanceof Error ? err.message : 'Network error' };
    }
  }, [getScheduledRecipients]);

  const checkAndFireScheduled = useCallback(async (
    cfg: ScheduleConfig,
    currentMembers: Member[],
    currentVisitors: Visitor[],
  ) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const todayDow = today.getDay();
    const todayDate = today.getDate();

    if (cfg.weeklyEnabled && todayDow === cfg.weeklyDay) {
      const lastSent = localStorage.getItem(LAST_WEEKLY_SENT_KEY);
      if (lastSent !== todayStr) {
        setScheduleStatus(prev => ({ ...prev, weekly: 'Sending weekly report…' }));
        const result = await sendScheduledReport('weekly', cfg, currentMembers, currentVisitors);
        if (result) {
          setScheduleStatus(prev => ({ ...prev, weekly: result.success ? `✓ Weekly report auto-sent today (${todayStr})` : `✗ ${result.message}` }));
        }
      } else {
        setScheduleStatus(prev => ({ ...prev, weekly: `✓ Weekly report already sent today (${todayStr})` }));
      }
    }

    if (cfg.monthlyEnabled && todayDate === cfg.monthlyDay) {
      const lastSent = localStorage.getItem(LAST_MONTHLY_SENT_KEY);
      if (lastSent !== todayStr) {
        setScheduleStatus(prev => ({ ...prev, monthly: 'Sending monthly report…' }));
        const result = await sendScheduledReport('monthly', cfg, currentMembers, currentVisitors);
        if (result) {
          setScheduleStatus(prev => ({ ...prev, monthly: result.success ? `✓ Monthly report auto-sent today (${todayStr})` : `✗ ${result.message}` }));
        }
      } else {
        setScheduleStatus(prev => ({ ...prev, monthly: `✓ Monthly report already sent today (${todayStr})` }));
      }
    }
  }, [sendScheduledReport]);

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/login');
      return;
    }
    if (!authLoading && user) {
      loadAllData();
      const saved = localStorage.getItem(SCHEDULE_STORAGE_KEY);
      const cfg: ScheduleConfig = saved ? { ...DEFAULT_SCHEDULE, ...JSON.parse(saved) } : DEFAULT_SCHEDULE;
      setSchedule(cfg);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  async function loadAllData() {
    setDataLoading(true);
    try {
      const [membersData, visitorsData] = await Promise.all([
        memberService.getAll(),
        visitorService.getAll(),
      ]);
      setMembers(membersData);
      setVisitors(visitorsData);

      const { data: staffData } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, role, created_at')
        .order('created_at', { ascending: false });
      setStaffList(
        (staffData || []).map((row: any) => ({
          id: row.id,
          email: row.email,
          fullName: row.full_name,
          role: row.role as StaffRole,
          createdAt: row.created_at,
        }))
      );

      const saved = localStorage.getItem(SCHEDULE_STORAGE_KEY);
      const cfg: ScheduleConfig = saved ? { ...DEFAULT_SCHEDULE, ...JSON.parse(saved) } : DEFAULT_SCHEDULE;
      checkAndFireScheduled(cfg, membersData, visitorsData);
    } finally {
      setDataLoading(false);
    }
  }

  function saveSchedule() {
    localStorage.setItem(SCHEDULE_STORAGE_KEY, JSON.stringify(schedule));
    setScheduleSaved(true);
    setTimeout(() => setScheduleSaved(false), 3000);
    checkAndFireScheduled(schedule, members, visitors);
  }

  async function sendNowScheduled(type: 'weekly' | 'monthly') {
    setSendingScheduled(type);
    setScheduleStatus(prev => ({ ...prev, [type]: `Sending ${type} report…` }));
    const result = await sendScheduledReport(type, schedule, members, visitors);
    if (result) {
      setScheduleStatus(prev => ({ ...prev, [type]: result.success ? `✓ ${result.message}` : `✗ ${result.message}` }));
    }
    setSendingScheduled(null);
  }

  async function sendReport(type: ReportType) {
    setSending(type);
    setResults(prev => ({ ...prev, [type]: { success: false, message: '' } }));
    const recipients: { email: string; name: string }[] = [
      { email: 'pastor@church.org', name: 'Pastor John' },
      { email: 'business@church.org', name: 'Business Office' },
    ];
    if (customEmail.trim()) recipients.push({ email: customEmail.trim(), name: 'Additional Recipient' });

    const subjectMap: Record<ReportType, string> = {
      weekly_attendance: `Weekly Attendance Summary — ${new Date().toDateString()}`,
      visitor_followup: `Visitor Follow-Up List — ${new Date().toDateString()}`,
      pipeline_status: `Intake Pipeline Status — ${new Date().toDateString()}`,
      department_roster: `Department Rosters — ${new Date().toDateString()}`,
      monthly_summary: `Monthly Summary — ${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}`,
    };
    const htmlMap: Record<ReportType, string> = {
      weekly_attendance: buildWeeklyAttendanceHtml(members),
      visitor_followup: buildVisitorFollowupHtml(visitors),
      pipeline_status: buildPipelineStatusHtml(members),
      department_roster: buildDepartmentRosterHtml(members),
      monthly_summary: buildMonthlySummaryHtml(members, visitors),
    };

    try {
      const res = await fetch('/api/send-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportType: type, subject: subjectMap[type], htmlContent: htmlMap[type], recipients }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResults(prev => ({ ...prev, [type]: { success: true, message: `Sent to ${recipients.length} recipient(s). ID: ${data.messageId || 'OK'}` } }));
      } else {
        setResults(prev => ({ ...prev, [type]: { success: false, message: data.error || 'Failed to send.' } }));
      }
    } catch (err: unknown) {
      setResults(prev => ({ ...prev, [type]: { success: false, message: err instanceof Error ? err.message : 'Network error' } }));
    } finally {
      setSending(null);
    }
  }

  // ── Filtered Data ──────────────────────────────────────────────────────
  const filteredMembers = members.filter(m => {
    const matchSearch = m.name.toLowerCase().includes(memberSearch.toLowerCase()) || m.email.toLowerCase().includes(memberSearch.toLowerCase());
    const matchStatus = memberStatusFilter === 'All' || m.status === memberStatusFilter;
    const matchPipeline = memberPipelineFilter === 'All' || m.pipelineStage === memberPipelineFilter;
    return matchSearch && matchStatus && matchPipeline;
  });

  const filteredVisitors = visitors.filter(v => {
    const matchSearch = v.name.toLowerCase().includes(visitorSearch.toLowerCase()) || v.phone.includes(visitorSearch) || v.email.toLowerCase().includes(visitorSearch.toLowerCase());
    const matchFollow = visitorFollowFilter === 'All' || (visitorFollowFilter === 'Pending' ? !v.followedUp : v.followedUp);
    return matchSearch && matchFollow;
  });

  const filteredStaff = staffList.filter(s => {
    const matchSearch = s.fullName.toLowerCase().includes(staffSearch.toLowerCase()) || s.email.toLowerCase().includes(staffSearch.toLowerCase());
    const matchRole = staffRoleFilter === 'All' || s.role === staffRoleFilter;
    return matchSearch && matchRole;
  });

  // ── Export Functions ───────────────────────────────────────────────────
  function exportMembers() {
    const headers = ['Name', 'Email', 'Phone', 'Status', 'Department', 'Pipeline Stage', 'Join Date', 'Last Visit', 'Attendance Count', 'NFC Tag ID', 'Notes'];
    const rows = filteredMembers.map(m => [[
      m.name, m.email, m.phone, m.status, m.department || '', PIPELINE_LABELS[m.pipelineStage],
      m.joinDate, m.lastVisit, m.attendanceDates.length, m.nfcTagId || '', m.notes,
    ]]);
    exportCSV(`member-tracking-${new Date().toISOString().split('T')[0]}.csv`, headers, rows);
  }

  function exportVisitors() {
    const headers = ['Name', 'Email', 'Phone', 'Address', 'Visit Date', 'First Time', 'How Heard', 'Interested In', 'Follow-Up Status', 'Prayer Request'];
    const rows = filteredVisitors.map(v => [[
      v.name, v.email, v.phone, v.address, v.date, v.firstTime ? 'Yes' : 'No',
      v.howHeard, v.interestedIn.join('; '), v.followedUp ? 'Followed Up' : 'Pending', v.prayerRequest,
    ]]);
    exportCSV(`visitor-intake-${new Date().toISOString().split('T')[0]}.csv`, headers, rows);
  }

  function exportStaff() {
    const headers = ['Full Name', 'Email', 'Role', 'Date Added'];
    const rows = filteredStaff.map(s => [[
      s.fullName, s.email, ROLE_LABELS[s.role], s.createdAt ? s.createdAt.split('T')[0] : '',
    ]]);
    exportCSV(`staff-roles-${new Date().toISOString().split('T')[0]}.csv`, headers, rows);
  }

  // ── Render ─────────────────────────────────────────────────────────────
  const REPORT_CONFIGS: ReportConfig[] = [
    { type: 'weekly_attendance', label: 'Weekly Attendance Summary', description: 'Members who attended in the past 7 days with department breakdown.', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg> },
    { type: 'visitor_followup', label: 'Visitor Follow-Up List', description: 'All visitors who have not yet been followed up, with contact info.', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg> },
    { type: 'pipeline_status', label: 'Intake Pipeline Status', description: 'Current status of all members across each pipeline stage.', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg> },
    { type: 'department_roster', label: 'Department Rosters', description: 'Full roster of all departments with member lists.', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg> },
    { type: 'monthly_summary', label: 'Monthly Summary', description: 'High-level overview of membership, attendance, and visitor metrics for the month.', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg> },
  ];

  if (authLoading) return (
    <div className="min-h-screen bg-sanctuary flex items-center justify-center">
      <div className="noise-overlay" aria-hidden="true" />
      <p className="font-mono text-sm text-silver-dim relative z-10">Loading…</p>
    </div>
  );

  if (!user) return null;

  const tabs: { id: ActiveTab; label: string; count?: number }[] = [
    { id: 'members', label: 'Member Tracking', count: members.length },
    { id: 'visitors', label: 'Visitor Intake', count: visitors.length },
    { id: 'staff', label: 'Staff & Roles', count: staffList.length },
    { id: 'email_reports', label: 'Email Reports' },
  ];

  return (
    <div className="min-h-screen bg-sanctuary">
      <div className="noise-overlay" aria-hidden="true" />
      <Header />

      <main className="max-w-[1400px] mx-auto px-6 py-12">
        {/* Page Header */}
        <div className="mb-8">
          <span className="section-label block mb-2">Compassion Check-in // Reports</span>
          <h1 className="text-silver font-black text-3xl tracking-tight uppercase">Reports</h1>
          <p className="text-silver-dim text-sm mt-1 font-light">View and export all church data — member tracking, visitor intake, and staff roles.</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border-strong border border-border-strong mb-8">
          {[
            { label: 'Total Members', value: members.length },
            { label: 'Active Members', value: members.filter(m => m.status === 'Active').length },
            { label: 'Total Visitors', value: visitors.length },
            { label: 'Pending Follow-Up', value: visitors.filter(v => !v.followedUp).length },
          ].map(stat => (
            <div key={stat.label} className="bento-cell px-6 py-5">
              <div className="stat-number text-2xl">{stat.value}</div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-silver-dim opacity-60 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-strong mb-8 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`font-mono text-[11px] tracking-widest uppercase px-6 py-3 border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-gold text-gold' :'border-transparent text-silver-dim hover:text-silver hover:border-border-strong'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`text-[10px] px-1.5 py-0.5 border font-mono ${activeTab === tab.id ? 'border-gold/40 text-gold bg-gold/10' : 'border-border-strong text-silver-dim'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── MEMBER TRACKING TAB ── */}
        {activeTab === 'members' && (
          <div>
            {/* Filters + Export */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6 flex-wrap">
              <input
                type="text"
                placeholder="Search by name or email…"
                value={memberSearch}
                onChange={e => setMemberSearch(e.target.value)}
                className="bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors flex-1 min-w-[200px]"
              />
              <select
                value={memberStatusFilter}
                onChange={e => setMemberStatusFilter(e.target.value as typeof memberStatusFilter)}
                className="bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 focus:outline-none focus:border-gold transition-colors"
              >
                <option value="All">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="New">New</option>
              </select>
              <select
                value={memberPipelineFilter}
                onChange={e => setMemberPipelineFilter(e.target.value as typeof memberPipelineFilter)}
                className="bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 focus:outline-none focus:border-gold transition-colors"
              >
                <option value="All">All Pipeline Stages</option>
                <option value="visitor">First Visit</option>
                <option value="membership_class">Membership Class</option>
                <option value="full_member">Full Member</option>
              </select>
              <button
                onClick={exportMembers}
                className="btn-gold text-[11px] px-5 py-2 font-mono tracking-widest inline-flex items-center gap-2 flex-shrink-0"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                Export CSV ({filteredMembers.length})
              </button>
            </div>

            {dataLoading ? (
              <div className="border border-border-strong py-16 text-center">
                <p className="font-mono text-sm text-silver-dim opacity-50">Loading members…</p>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="border border-border-strong py-16 text-center">
                <p className="font-mono text-sm text-silver-dim opacity-50">No members found.</p>
              </div>
            ) : (
              <div className="border border-border-strong overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-border-strong bg-vestment">
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-gold">Name</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Email</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Phone</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Status</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Department</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Pipeline</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Join Date</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Last Visit</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Attendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((m, i) => (
                      <tr key={m.id} className={`border-b border-border-strong/50 hover:bg-vestment/50 transition-colors ${i % 2 === 0 ? '' : 'bg-vestment/20'}`}>
                        <td className="px-4 py-3 text-silver text-sm font-medium">{m.name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{m.email || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{m.phone || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`font-mono text-[10px] tracking-wider uppercase px-2 py-0.5 border ${
                            m.status === 'Active' ? 'text-green-400 border-green-400/40 bg-green-400/10' :
                            m.status === 'New'? 'text-gold border-gold/40 bg-gold/10' : 'text-silver-dim border-border-strong'
                          }`}>{m.status}</span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{m.department || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`font-mono text-[10px] tracking-wider uppercase px-2 py-0.5 border ${
                            m.pipelineStage === 'full_member' ? 'text-green-400 border-green-400/40 bg-green-400/10' :
                            m.pipelineStage === 'membership_class'? 'text-blue-400 border-blue-400/40 bg-blue-400/10' : 'text-gold border-gold/40 bg-gold/10'
                          }`}>{PIPELINE_LABELS[m.pipelineStage]}</span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{m.joinDate || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{m.lastVisit || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{m.attendanceDates.length} visits</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="font-mono text-[10px] text-silver-dim opacity-50 mt-3">Showing {filteredMembers.length} of {members.length} members</p>
          </div>
        )}

        {/* ── VISITOR INTAKE TAB ── */}
        {activeTab === 'visitors' && (
          <div>
            {/* Filters + Export */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6 flex-wrap">
              <input
                type="text"
                placeholder="Search by name, phone, or email…"
                value={visitorSearch}
                onChange={e => setVisitorSearch(e.target.value)}
                className="bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors flex-1 min-w-[200px]"
              />
              <select
                value={visitorFollowFilter}
                onChange={e => setVisitorFollowFilter(e.target.value as typeof visitorFollowFilter)}
                className="bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 focus:outline-none focus:border-gold transition-colors"
              >
                <option value="All">All Follow-Up Status</option>
                <option value="Pending">Pending</option>
                <option value="Followed Up">Followed Up</option>
              </select>
              <button
                onClick={exportVisitors}
                className="btn-gold text-[11px] px-5 py-2 font-mono tracking-widest inline-flex items-center gap-2 flex-shrink-0"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                Export CSV ({filteredVisitors.length})
              </button>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-px bg-border-strong border border-border-strong mb-6">
              {[
                { label: 'Total Visitors', value: visitors.length },
                { label: 'Awaiting Follow-Up', value: visitors.filter(v => !v.followedUp).length },
                { label: 'Followed Up', value: visitors.filter(v => v.followedUp).length },
              ].map(stat => (
                <div key={stat.label} className="bento-cell px-6 py-4">
                  <div className="stat-number text-2xl">{stat.value}</div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-silver-dim opacity-60 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {dataLoading ? (
              <div className="border border-border-strong py-16 text-center">
                <p className="font-mono text-sm text-silver-dim opacity-50">Loading visitors…</p>
              </div>
            ) : filteredVisitors.length === 0 ? (
              <div className="border border-border-strong py-16 text-center">
                <p className="font-mono text-sm text-silver-dim opacity-50">No visitors found.</p>
              </div>
            ) : (
              <div className="border border-border-strong overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-border-strong bg-vestment">
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-gold">Name</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Phone</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Email</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Visit Date</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">First Time</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">How Heard</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Interests</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Follow-Up</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Prayer Request</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisitors.map((v, i) => (
                      <tr key={v.id} className={`border-b border-border-strong/50 hover:bg-vestment/50 transition-colors ${i % 2 === 0 ? '' : 'bg-vestment/20'}`}>
                        <td className="px-4 py-3 text-silver text-sm font-medium">{v.name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{v.phone || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{v.email || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{v.date}</td>
                        <td className="px-4 py-3">
                          {v.firstTime && <span className="font-mono text-[10px] tracking-wider uppercase px-2 py-0.5 border border-gold/40 text-gold bg-gold/10">First Time</span>}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{v.howHeard || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {v.interestedIn.length > 0
                              ? v.interestedIn.map(i => <span key={i} className="font-mono text-[9px] tracking-wider uppercase px-1.5 py-0.5 border border-border-strong text-silver-dim">{i}</span>)
                              : <span className="text-silver-dim text-xs opacity-50">—</span>
                            }
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-mono text-[10px] tracking-wider uppercase px-2 py-0.5 border ${v.followedUp ? 'border-green-400/40 text-green-400 bg-green-400/10' : 'border-silver-dim/30 text-silver-dim'}`}>
                            {v.followedUp ? 'Done' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim max-w-[200px] truncate" title={v.prayerRequest}>{v.prayerRequest || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="font-mono text-[10px] text-silver-dim opacity-50 mt-3">Showing {filteredVisitors.length} of {visitors.length} visitors</p>
          </div>
        )}

        {/* ── STAFF & ROLES TAB ── */}
        {activeTab === 'staff' && (
          <div>
            {/* Filters + Export */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6 flex-wrap">
              <input
                type="text"
                placeholder="Search by name or email…"
                value={staffSearch}
                onChange={e => setStaffSearch(e.target.value)}
                className="bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors flex-1 min-w-[200px]"
              />
              <select
                value={staffRoleFilter}
                onChange={e => setStaffRoleFilter(e.target.value as typeof staffRoleFilter)}
                className="bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 focus:outline-none focus:border-gold transition-colors"
              >
                <option value="All">All Roles</option>
                <option value="admin">Admin</option>
                <option value="pastor">Pastor</option>
                <option value="church_staff">Church Staff</option>
                <option value="greeter">Greeter</option>
                <option value="usher">Usher</option>
                <option value="business_office">Business Office</option>
              </select>
              <button
                onClick={exportStaff}
                className="btn-gold text-[11px] px-5 py-2 font-mono tracking-widest inline-flex items-center gap-2 flex-shrink-0"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                Export CSV ({filteredStaff.length})
              </button>
            </div>

            {/* Role breakdown */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-border-strong border border-border-strong mb-6">
              {(['admin', 'pastor', 'church_staff', 'greeter', 'usher', 'business_office'] as StaffRole[]).map(role => (
                <div key={role} className="bento-cell px-4 py-4">
                  <div className="stat-number text-xl">{staffList.filter(s => s.role === role).length}</div>
                  <div className={`font-mono text-[9px] tracking-widest uppercase mt-1 ${ROLE_COLORS[role].split(' ')[0]}`}>{ROLE_LABELS[role]}</div>
                </div>
              ))}
            </div>

            {dataLoading ? (
              <div className="border border-border-strong py-16 text-center">
                <p className="font-mono text-sm text-silver-dim opacity-50">Loading staff…</p>
              </div>
            ) : filteredStaff.length === 0 ? (
              <div className="border border-border-strong py-16 text-center">
                <p className="font-mono text-sm text-silver-dim opacity-50">No staff found.</p>
              </div>
            ) : (
              <div className="border border-border-strong overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-border-strong bg-vestment">
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-gold">Full Name</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Email</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Role</th>
                      <th className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim">Date Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStaff.map((s, i) => (
                      <tr key={s.id} className={`border-b border-border-strong/50 hover:bg-vestment/50 transition-colors ${i % 2 === 0 ? '' : 'bg-vestment/20'}`}>
                        <td className="px-4 py-3 text-silver text-sm font-medium">{s.fullName}</td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{s.email}</td>
                        <td className="px-4 py-3">
                          <span className={`font-mono text-[10px] tracking-wider uppercase px-2 py-0.5 border ${ROLE_COLORS[s.role]}`}>
                            {ROLE_LABELS[s.role]}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-silver-dim">{s.createdAt ? s.createdAt.split('T')[0] : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="font-mono text-[10px] text-silver-dim opacity-50 mt-3">Showing {filteredStaff.length} of {staffList.length} staff members</p>
          </div>
        )}

        {/* ── EMAIL REPORTS TAB ── */}
        {activeTab === 'email_reports' && (
          <div>
            {/* Scheduled Reports */}
            <div className="border border-gold/40 bg-vestment mb-10">
              <div className="px-6 py-4 border-b border-gold/20 flex items-center gap-3">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4A843" strokeWidth="1.5" className="flex-shrink-0">
                  <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                </svg>
                <h2 className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold">Scheduled Auto-Reports</h2>
                <span className="ml-auto font-mono text-[10px] text-silver-dim opacity-50">Sends automatically on the configured days via Brevo</span>
              </div>

              <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Weekly Schedule */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setSchedule(s => ({ ...s, weeklyEnabled: !s.weeklyEnabled }))} className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${schedule.weeklyEnabled ? 'bg-gold' : 'bg-border-strong'}`} aria-label="Toggle weekly report">
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${schedule.weeklyEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                    <span className="font-mono text-[11px] tracking-widest uppercase text-silver">Weekly Report</span>
                    {schedule.weeklyEnabled && <span className="font-mono text-[10px] text-gold opacity-80">Active</span>}
                  </div>
                  <p className="text-silver-dim text-xs font-light">Sends the <strong className="text-silver font-medium">Weekly Attendance Summary</strong> every week on the selected day.</p>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-2">Send Every</label>
                    <div className="flex flex-wrap gap-2">
                      {DAY_NAMES.map((day, idx) => (
                        <button key={day} type="button" onClick={() => setSchedule(s => ({ ...s, weeklyDay: idx }))} className={`font-mono text-[10px] tracking-wider px-3 py-1.5 border transition-colors ${schedule.weeklyDay === idx ? 'border-gold bg-gold/10 text-gold' : 'border-border-strong text-silver-dim hover:border-gold/40 hover:text-silver'}`}>
                          {day.slice(0, 3).toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                  {scheduleStatus.weekly && (
                    <div className={`border px-4 py-2 font-mono text-xs ${scheduleStatus.weekly.startsWith('✓') ? 'border-green-400/40 bg-green-400/10 text-green-400' : scheduleStatus.weekly.includes('Sending') ? 'border-gold/40 bg-gold/10 text-gold' : 'border-red-400/40 bg-red-400/10 text-red-400'}`}>
                      {scheduleStatus.weekly}
                    </div>
                  )}
                  <button type="button" onClick={() => sendNowScheduled('weekly')} disabled={sendingScheduled !== null} className="btn-gold text-[10px] px-4 py-2 font-mono tracking-widest disabled:opacity-50 inline-flex items-center gap-2">
                    {sendingScheduled === 'weekly' ? (<><svg className="animate-spin" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>Sending…</>) : (<><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>Send Weekly Now</>)}
                  </button>
                </div>

                {/* Monthly Schedule */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setSchedule(s => ({ ...s, monthlyEnabled: !s.monthlyEnabled }))} className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${schedule.monthlyEnabled ? 'bg-gold' : 'bg-border-strong'}`} aria-label="Toggle monthly report">
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${schedule.monthlyEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                    <span className="font-mono text-[11px] tracking-widest uppercase text-silver">Monthly Report</span>
                    {schedule.monthlyEnabled && <span className="font-mono text-[10px] text-gold opacity-80">Active</span>}
                  </div>
                  <p className="text-silver-dim text-xs font-light">Sends the <strong className="text-silver font-medium">Monthly Summary</strong> on the selected day of each month.</p>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-2">Send on Day of Month</label>
                    <div className="flex items-center gap-3">
                      <input type="number" min={1} max={28} value={schedule.monthlyDay} onChange={e => setSchedule(s => ({ ...s, monthlyDay: Math.min(28, Math.max(1, parseInt(e.target.value) || 1)) }))} className="w-20 bg-sanctuary border border-border-strong text-silver font-mono text-sm px-3 py-2 focus:outline-none focus:border-gold transition-colors text-center" />
                      <span className="text-silver-dim text-xs font-mono">of each month (1–28)</span>
                    </div>
                  </div>
                  {scheduleStatus.monthly && (
                    <div className={`border px-4 py-2 font-mono text-xs ${scheduleStatus.monthly.startsWith('✓') ? 'border-green-400/40 bg-green-400/10 text-green-400' : scheduleStatus.monthly.includes('Sending') ? 'border-gold/40 bg-gold/10 text-gold' : 'border-red-400/40 bg-red-400/10 text-red-400'}`}>
                      {scheduleStatus.monthly}
                    </div>
                  )}
                  <button type="button" onClick={() => sendNowScheduled('monthly')} disabled={sendingScheduled !== null} className="btn-gold text-[10px] px-4 py-2 font-mono tracking-widest disabled:opacity-50 inline-flex items-center gap-2">
                    {sendingScheduled === 'monthly' ? (<><svg className="animate-spin" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>Sending…</>) : (<><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>Send Monthly Now</>)}
                  </button>
                </div>
              </div>

              {/* Recipients config */}
              <div className="px-6 pb-6 border-t border-border-subtle pt-6">
                <p className="font-mono text-[10px] tracking-widest uppercase text-silver-dim mb-4">Scheduled Report Recipients</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                  <div>
                    <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Pastor Name</label>
                    <input type="text" value={schedule.pastorName} onChange={e => setSchedule(s => ({ ...s, pastorName: e.target.value }))} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 focus:outline-none focus:border-gold transition-colors" placeholder="Pastor Name" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Pastor Email</label>
                    <input type="email" value={schedule.pastorEmail} onChange={e => setSchedule(s => ({ ...s, pastorEmail: e.target.value }))} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 focus:outline-none focus:border-gold transition-colors" placeholder="pastor@church.org" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Business Office Name</label>
                    <input type="text" value={schedule.businessName} onChange={e => setSchedule(s => ({ ...s, businessName: e.target.value }))} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 focus:outline-none focus:border-gold transition-colors" placeholder="Business Office" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Business Office Email</label>
                    <input type="email" value={schedule.businessEmail} onChange={e => setSchedule(s => ({ ...s, businessEmail: e.target.value }))} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 focus:outline-none focus:border-gold transition-colors" placeholder="business@church.org" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button type="button" onClick={saveSchedule} className="btn-gold text-[11px] px-6 py-2.5 font-mono tracking-widest inline-flex items-center gap-2">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
                    Save Schedule
                  </button>
                  {scheduleSaved && <span className="font-mono text-[11px] text-green-400">✓ Schedule saved</span>}
                  <span className="font-mono text-[10px] text-silver-dim opacity-50 ml-auto">Schedule is checked automatically each time this page loads</span>
                </div>
              </div>
            </div>

            {/* Recipients info */}
            <div className="border border-border-strong bg-vestment px-6 py-4 mb-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <p className="font-mono text-[10px] tracking-widest uppercase text-silver-dim opacity-60 mb-1">Default Recipients</p>
                <div className="flex flex-wrap gap-3">
                  {[{ name: 'Pastor John', email: 'pastor@church.org' }, { name: 'Business Office', email: 'business@church.org' }].map(r => (
                    <span key={r.email} className="font-mono text-[11px] text-silver border border-border-strong px-3 py-1">{r.name} <span className="text-silver-dim opacity-60">({r.email})</span></span>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 w-full sm:w-64">
                <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Additional Recipient (optional)</label>
                <input type="email" placeholder="extra@email.com" value={customEmail} onChange={e => setCustomEmail(e.target.value)} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors" />
              </div>
            </div>

            {/* Report cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border-strong border border-border-strong">
              {REPORT_CONFIGS.map(config => {
                const result = results[config.type];
                const isSending = sending === config.type;
                return (
                  <div key={config.type} className="bento-cell p-6 flex flex-col gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold flex-shrink-0">{config.icon}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-silver font-semibold text-sm mb-1">{config.label}</h3>
                        <p className="text-silver-dim text-xs font-light leading-relaxed">{config.description}</p>
                      </div>
                    </div>
                    {result && (
                      <div className={`border px-4 py-2 font-mono text-xs ${result.success ? 'border-green-400/40 bg-green-400/10 text-green-400' : 'border-red-400/40 bg-red-400/10 text-red-400'}`}>
                        {result.success ? '✓ ' : '✗ '}{result.message}
                      </div>
                    )}
                    <button onClick={() => sendReport(config.type)} disabled={isSending || sending !== null} className="btn-gold text-[11px] px-6 py-3 font-mono tracking-widest self-start disabled:opacity-50 inline-flex items-center gap-2">
                      {isSending ? (<><svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>Sending…</>) : (<><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>Send via Brevo</>)}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
